import React, { createContext, useContext, useId } from 'react';
import { cubicBezier, motion, MotionValue, useMotionValue, useTransform } from 'motion/react';
import { alpha } from '@mui/material';
import { ease, fontFamilyDisplay, glory, grey } from '../../../theme/tokens';

/**
 * A small-block V8, front view, as an exploded parts diagram.
 *
 * Two progress values drive it, both 0 → 1: `entry` draws the line art in as
 * the section scrolls into view, `teardown` takes the engine apart while the
 * section is pinned. The block turns to an x-ray first, so the pistons, rods
 * and crank show; then each part in `TEARDOWN` comes off along its own axis
 * with a numbered callout, top to bottom, the way it comes apart on a stand.
 *
 * Geometry: the crank centre is (400, 500). Each cylinder bank is drawn in its
 * own frame (x out along the bore, y towards the valley), rotated 45° either
 * side of vertical, so a part comes off its bank by sliding along x.
 */

/** The parts in teardown order, with the slice of `teardown` each one moves in. */
export const TEARDOWN = [
  { name: 'Air cleaner', range: [0.08, 0.22] },
  { name: 'Four-barrel carburetor', range: [0.16, 0.3] },
  { name: 'Intake manifold', range: [0.24, 0.38] },
  { name: 'Valve covers', range: [0.32, 0.46] },
  { name: 'Cylinder heads', range: [0.4, 0.54] },
  { name: 'Exhaust headers', range: [0.46, 0.6] },
  { name: 'Pistons & rods', range: [0.54, 0.68] },
  { name: 'Crankshaft', range: [0.62, 0.76] },
  { name: 'Oil pan', range: [0.7, 0.84] },
] as const;

/** The block goes see-through before anything comes off. */
const XRAY: [number, number] = [0.02, 0.1];

/**
 * The camera: close on the assembled engine, pulling back to the full diagram
 * while the air cleaner lifts off, so the parts never leave the frame.
 */
const ZOOM = {
  range: [0, 0.2],
  scale: [1.4, 1],
  origin: '400px 420px',
} as const;

/** anime.js-style overshoot: the part flies a touch past its mark and settles. */
const overshoot = cubicBezier(...ease.emphasis);

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

const S = Math.SQRT1_2;
const CX = 400;
const CY = 500;
type Bank = 'L' | 'R';
const AXIS = { L: { x: -S, y: -S }, R: { x: S, y: -S } };
const TO_VALLEY = { L: { x: S, y: -S }, R: { x: -S, y: -S } };

/** A point in a bank's frame (d out along the bore, w towards the valley), in page coordinates. */
const at = (bank: Bank, d: number, w: number) => ({
  x: CX + AXIS[bank].x * d + TO_VALLEY[bank].x * w,
  y: CY + AXIS[bank].y * d + TO_VALLEY[bank].y * w,
});

const BANK_FRAME = {
  L: `translate(${CX} ${CY}) rotate(225)`,
  // Mirrored, so the same drawing serves both banks.
  R: `translate(${CX} ${CY}) rotate(-45) scale(1 -1)`,
};

const pointList = (points: { x: number; y: number }[]) =>
  points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

const BLOCK = pointList([
  at('L', 230, -55),
  at('L', 230, 55),
  at('L', 120, 55),
  at('R', 120, 55),
  at('R', 230, 55),
  at('R', 230, -55),
  at('R', 70, -55),
  { x: at('R', 70, -55).x, y: 575 },
  { x: at('L', 70, -55).x, y: 575 },
  at('L', 70, -55),
]);

/** The rod's big end, shared by both banks' rods: the crank throw at top dead centre, in a bank's frame. */
const THROW = { x: 30 * S, y: 30 * S };

/** A connecting rod from the wrist pin (186, 0) to the crank throw, tapering towards the pin. */
const ROD = (() => {
  const dx = 186 - THROW.x;
  const dy = 0 - THROW.y;
  const length = Math.hypot(dx, dy);
  const nx = -dy / length;
  const ny = dx / length;
  return pointList([
    { x: 186 + nx * 7, y: ny * 7 },
    { x: THROW.x + nx * 12, y: THROW.y + ny * 12 },
    { x: THROW.x - nx * 12, y: THROW.y - ny * 12 },
    { x: 186 - nx * 7, y: -ny * 7 },
  ]);
})();

// ---------------------------------------------------------------------------
// Ink: every shape draws its outline in, then fills
// ---------------------------------------------------------------------------

interface Ink {
  draw: MotionValue<number>;
  fill: MotionValue<number>;
}

const InkContext = createContext<Ink | null>(null);

const LINE = alpha(grey[50], 0.72);
const FAINT = alpha(grey[50], 0.4);

const SVG_TAGS = {
  path: motion.path,
  rect: motion.rect,
  circle: motion.circle,
  polygon: motion.polygon,
  line: motion.line,
} as const;

type ShapeProps = { as: keyof typeof SVG_TAGS; dashed?: boolean } & Record<string, unknown>;

/** One line-art shape. Dashed (hidden) lines fade instead of drawing. */
const Shape: React.FC<ShapeProps> = ({ as, dashed, style, ...props }) => {
  const ink = useContext(InkContext)!;
  const Tag = SVG_TAGS[as] as React.ElementType;
  return (
    <Tag
      stroke={LINE}
      strokeWidth={1.4}
      strokeLinejoin="round"
      strokeLinecap="round"
      fill="none"
      {...(dashed ? { strokeDasharray: '6 5' } : {})}
      {...props}
      style={{
        ...(dashed ? { opacity: ink.draw } : { pathLength: ink.draw }),
        fillOpacity: ink.fill,
        ...(style as object),
      }}
    />
  );
};

/** Draw-in and fill timing for a part, from the entry progress. */
function useInk(entry: MotionValue<number>, order: number): Ink {
  const start = 0.12 + order * 0.05;
  const draw = useTransform(entry, [start, start + 0.35], [0, 1], {
    clamp: true,
  });
  const fill = useTransform(entry, [0.55 + order * 0.025, 0.8 + order * 0.025], [0, 1], { clamp: true });
  return { draw, fill };
}

/** A part's teardown: eased travel (0 → 1, with overshoot) and when its callout shows. */
function usePart(teardown: MotionValue<number>, index: number) {
  const [from, to] = TEARDOWN[index].range;
  const progress = useTransform(teardown, [from, to], [0, 1], { clamp: true });
  const travel = useTransform(progress, [0, 1], [0, 1], { ease: overshoot });
  const callout = useTransform(progress, [0.55, 1], [0, 1], { clamp: true });
  return { travel, callout };
}

/** A part's travel in page units. */
const useScaled = (travel: MotionValue<number>, distance: number) => useTransform(travel, (t) => t * distance);

// ---------------------------------------------------------------------------
// Callouts
// ---------------------------------------------------------------------------

const Callout: React.FC<{
  number: number;
  travel: MotionValue<number>;
  visible: MotionValue<number>;
  /** Where the leader line touches the part, at a given travel. */
  anchor: (t: number) => { x: number; y: number };
  offset: [number, number];
}> = ({ number, travel, visible, anchor, offset }) => {
  const ax = useTransform(travel, (t) => anchor(t).x);
  const ay = useTransform(travel, (t) => anchor(t).y);
  const bx = useTransform(ax, (x) => x + offset[0]);
  const by = useTransform(ay, (y) => y + offset[1]);
  const scale = useTransform(visible, [0, 1], [0.4, 1]);
  return (
    <motion.g style={{ opacity: visible }}>
      <motion.line x1={ax} y1={ay} x2={bx} y2={by} stroke={glory[400]} strokeWidth={1.25} />
      <motion.circle cx={ax} cy={ay} r={3.5} fill={glory[400]} />
      <motion.g style={{ x: bx, y: by, scale }}>
        <circle r={15} fill={glory[600]} stroke={alpha(grey[0], 0.85)} strokeWidth={1.5} />
        <text
          textAnchor="middle"
          dominantBaseline="central"
          fill={grey[0]}
          fontFamily={fontFamilyDisplay}
          fontWeight={800}
          fontSize={17}
        >
          {number}
        </text>
      </motion.g>
    </motion.g>
  );
};

// ---------------------------------------------------------------------------
// Parts
// ---------------------------------------------------------------------------

const Bore: React.FC = () => (
  <>
    <Shape as="rect" x={95} y={-36} width={135} height={72} dashed />
    <Shape as="line" x1={100} y1={-47} x2={226} y2={-47} dashed stroke={FAINT} />
    <Shape as="line" x1={100} y1={47} x2={226} y2={47} dashed stroke={FAINT} />
  </>
);

const Head: React.FC = () => (
  <>
    <Shape as="polygon" points="230,-62 276,-62 285,-54 285,54 276,62 230,62" fill={grey[700]} />
    <Shape as="rect" x={246} y={-62} width={22} height={12} fill={grey[950]} />
    <Shape as="rect" x={244} y={50} width={26} height={12} fill={grey[950]} />
    <Shape as="line" x1={238} y1={-62} x2={234} y2={-78} />
    <Shape as="circle" cx={233} cy={-82} r={4.5} fill={grey[300]} />
    {[-28, 28].map((w) => (
      <React.Fragment key={w}>
        <Shape as="circle" cx={244} cy={w} r={3} />
        <Shape as="circle" cx={271} cy={w} r={3} />
      </React.Fragment>
    ))}
  </>
);

const ValveCover: React.FC<{ breather?: boolean; gradient: string }> = ({ breather, gradient }) => (
  <>
    <Shape as="path" d="M285,-58 L306,-58 Q320,-58 320,-44 L320,44 Q320,58 306,58 L285,58 Z" fill={gradient} />
    {[293, 300, 307].map((x) => (
      <Shape key={x} as="line" x1={x} y1={-46} x2={x} y2={46} stroke={alpha(grey[0], 0.35)} />
    ))}
    {breather && <Shape as="rect" x={320} y={-40} width={12} height={18} rx={3} fill={grey[300]} />}
  </>
);

const Piston: React.FC = () => (
  <>
    <Shape as="polygon" points={ROD} fill={grey[500]} />
    <Shape as="circle" cx={THROW.x} cy={THROW.y} r={17} fill={grey[500]} />
    <Shape as="rect" x={170} y={-33} width={42} height={66} rx={3} fill={grey[300]} />
    <Shape as="line" x1={204} y1={-33} x2={204} y2={33} stroke={grey[600]} />
    <Shape as="line" x1={198} y1={-33} x2={198} y2={33} stroke={grey[600]} />
    <Shape as="circle" cx={186} cy={0} r={7} fill={grey[500]} />
  </>
);

/** Three primary tubes sweeping into a collector, each a tube with a lit core. */
const Header: React.FC = () => {
  const tubes = [-16, 0, 16].map((o) => `M${257 + o},-62 C${257 + o},-112 ${224 + o * 0.7},-158 178,-194`);
  const collector = 'M178,-194 L150,-222';
  return (
    <>
      {tubes.map((d) => (
        <Shape key={`${d}-outer`} as="path" d={d} stroke={grey[600]} strokeWidth={14} />
      ))}
      {tubes.map((d) => (
        <Shape key={`${d}-core`} as="path" d={d} stroke={alpha(grey[200], 0.8)} strokeWidth={4} />
      ))}
      <Shape as="path" d={collector} stroke={grey[600]} strokeWidth={30} />
      <Shape as="path" d={collector} stroke={alpha(grey[200], 0.8)} strokeWidth={6} />
      {/* Flange across the collector's mouth */}
      <Shape as="line" x1={134} y1={-214} x2={158} y2={-238} stroke={grey[300]} strokeWidth={6} />
    </>
  );
};

// ---------------------------------------------------------------------------
// The engine
// ---------------------------------------------------------------------------

const V8Drawing: React.FC<{
  entry: MotionValue<number>;
  teardown: MotionValue<number>;
}> = ({ entry, teardown }) => {
  const id = useId().replace(/:/g, '');
  const chrome = `url(#${id}-chrome)`;
  const red = `url(#${id}-red)`;

  // Draw-in order: the block first, the dress-up parts last.
  const blockInk = useInk(entry, 0);
  const headInk = useInk(entry, 1);
  const headerInk = useInk(entry, 2);
  const coverInk = useInk(entry, 2);
  const intakeInk = useInk(entry, 3);
  const panInk = useInk(entry, 3);
  const carbInk = useInk(entry, 4);
  const cleanerInk = useInk(entry, 5);
  const one = useMotionValue(1);

  const zoom = useTransform(teardown, [...ZOOM.range], [...ZOOM.scale], {
    clamp: true,
    ease: cubicBezier(...ease.inOut),
  });

  // The x-ray: the block's skin thins and its insides show.
  const xray = useTransform(teardown, XRAY, [0, 1], { clamp: true });
  const blockFill = useTransform([blockInk.fill, xray], ([f, x]: number[]) => f * (1 - 0.8 * x));
  const insideInk: Ink = { draw: one, fill: one };

  const cleaner = usePart(teardown, 0);
  const carb = usePart(teardown, 1);
  const intake = usePart(teardown, 2);
  const covers = usePart(teardown, 3);
  const heads = usePart(teardown, 4);
  const headers = usePart(teardown, 5);
  const pistons = usePart(teardown, 6);
  const crank = usePart(teardown, 7);
  const pan = usePart(teardown, 8);

  const cleanerY = useScaled(cleaner.travel, -230);
  const carbY = useScaled(carb.travel, -150);
  const intakeY = useScaled(intake.travel, -95);
  const coverX = useScaled(covers.travel, 140);
  const headX = useScaled(heads.travel, 105);
  const headerX = useScaled(headers.travel, -15);
  const headerY = useScaled(headers.travel, -115);
  const pistonX = useScaled(pistons.travel, 72);
  const crankY = useScaled(crank.travel, 115);
  const panY = useScaled(pan.travel, 200);

  const bank = (side: Bank, children: React.ReactNode) => (
    <g key={side} transform={BANK_FRAME[side]}>
      {children}
    </g>
  );

  return (
    <svg
      viewBox="0 -110 800 980"
      role="img"
      aria-label="Exploded view of a V8 engine"
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        overflow: 'visible',
      }}
    >
      <defs>
        <linearGradient id={`${id}-chrome`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={grey[0]} />
          <stop offset="0.45" stopColor={grey[300]} />
          <stop offset="1" stopColor={grey[500]} />
        </linearGradient>
        <linearGradient id={`${id}-red`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={glory[700]} />
          <stop offset="0.6" stopColor={glory[500]} />
          <stop offset="1" stopColor={glory[600]} />
        </linearGradient>
      </defs>

      <motion.g
        style={{
          scale: zoom,
          transformBox: 'view-box',
          transformOrigin: ZOOM.origin,
        }}
      >
        {/* Headers sit behind the block. */}
        <InkContext.Provider value={headerInk}>
          {(['L', 'R'] as const).map((side) =>
            bank(
              side,
              <motion.g style={{ x: headerX, y: headerY }}>
                <Header />
              </motion.g>,
            ),
          )}
        </InkContext.Provider>

        {/* Block */}
        <InkContext.Provider value={{ draw: blockInk.draw, fill: blockFill }}>
          <Shape as="polygon" points={BLOCK} fill={grey[800]} />
          <Shape as="line" x1={at('L', 70, -55).x} y1={560} x2={at('R', 70, -55).x} y2={560} stroke={FAINT} />
          {(['L', 'R'] as const).map((side) =>
            bank(side, <Shape as="circle" cx={150} cy={-55} r={8} fill={grey[700]} />),
          )}
        </InkContext.Provider>

        {/* Inside the block, shown by the x-ray */}
        <motion.g style={{ opacity: xray }}>
          <InkContext.Provider value={insideInk}>
            {(['L', 'R'] as const).map((side) => bank(side, <Bore />))}
            <motion.g style={{ y: crankY }}>
              <Shape
                as="path"
                d="M338,500 A62,62 0 0 0 462,500 L440,500 L430,486 L370,486 L360,500 Z"
                fill={grey[600]}
              />
              <Shape as="rect" x={378} y={462} width={44} height={40} rx={10} fill={grey[600]} />
              <Shape as="circle" cx={400} cy={500} r={22} fill={grey[500]} />
              <Shape as="circle" cx={400} cy={500} r={8} fill={grey[800]} />
              <Shape as="circle" cx={400} cy={470} r={15} fill={grey[400]} />
            </motion.g>
            {(['L', 'R'] as const).map((side) =>
              bank(
                side,
                <motion.g style={{ x: pistonX }}>
                  <Piston />
                </motion.g>,
              ),
            )}
          </InkContext.Provider>
        </motion.g>

        {/* Heads and valve covers */}
        <InkContext.Provider value={headInk}>
          {(['L', 'R'] as const).map((side) =>
            bank(
              side,
              <motion.g style={{ x: headX }}>
                <Head />
              </motion.g>,
            ),
          )}
        </InkContext.Provider>
        <InkContext.Provider value={coverInk}>
          {(['L', 'R'] as const).map((side) =>
            bank(
              side,
              <motion.g style={{ x: coverX }}>
                <ValveCover breather={side === 'L'} gradient={red} />
              </motion.g>,
            ),
          )}
        </InkContext.Provider>

        {/* Intake manifold */}
        <InkContext.Provider value={intakeInk}>
          <motion.g style={{ y: intakeY }}>
            <Shape as="polygon" points="262,258 538,258 523,298 446,370 354,370 277,298" fill={grey[600]} />
            <Shape as="rect" x={350} y={240} width={100} height={18} fill={grey[600]} />
            <Shape as="line" x1={300} y1={268} x2={344} y2={330} stroke={alpha(grey[0], 0.3)} />
            <Shape as="line" x1={500} y1={268} x2={456} y2={330} stroke={alpha(grey[0], 0.3)} />
            <Shape as="circle" cx={400} cy={306} r={13} fill={grey[500]} />
          </motion.g>
        </InkContext.Provider>

        {/* Four-barrel carburetor */}
        <InkContext.Provider value={carbInk}>
          <motion.g style={{ y: carbY }}>
            <Shape as="rect" x={352} y={232} width={96} height={9} fill={grey[600]} />
            <Shape as="rect" x={362} y={196} width={76} height={38} rx={3} fill={grey[500]} />
            <Shape as="line" x1={381} y1={200} x2={381} y2={230} stroke={alpha(grey[0], 0.3)} />
            <Shape as="line" x1={400} y1={200} x2={400} y2={230} stroke={alpha(grey[0], 0.3)} />
            <Shape as="line" x1={419} y1={200} x2={419} y2={230} stroke={alpha(grey[0], 0.3)} />
            <Shape as="rect" x={354} y={206} width={8} height={12} fill={grey[400]} />
            <Shape as="circle" cx={447} cy={222} r={6} fill={grey[400]} />
          </motion.g>
        </InkContext.Provider>

        {/* Air cleaner */}
        <InkContext.Provider value={cleanerInk}>
          <motion.g style={{ y: cleanerY }}>
            <Shape as="path" d="M392,163 L387,147 L400,152 L413,147 L408,163 Z" fill={chrome} />
            <Shape as="rect" x={262} y={162} width={276} height={34} rx={12} fill={chrome} />
            <Shape as="line" x1={272} y1={170} x2={528} y2={170} stroke={alpha(grey[950], 0.35)} />
            <Shape as="line" x1={272} y1={188} x2={528} y2={188} stroke={alpha(grey[950], 0.35)} />
            <motion.text
              x={400}
              y={180}
              textAnchor="middle"
              dominantBaseline="central"
              fill={glory[600]}
              fontFamily={fontFamilyDisplay}
              fontWeight={800}
              fontSize={16}
              letterSpacing={2}
              style={{ opacity: cleanerInk.fill }}
            >
              ★ V8 ★
            </motion.text>
          </motion.g>
        </InkContext.Provider>

        {/* Oil pan */}
        <InkContext.Provider value={panInk}>
          <motion.g style={{ y: panY }}>
            <Shape
              as="path"
              d="M300,575 L500,575 L497,590 L480,596 L474,640 L326,640 L320,596 L303,590 Z"
              fill={grey[900]}
            />
            <Shape as="line" x1={334} y1={612} x2={466} y2={612} stroke={FAINT} />
            <Shape as="line" x1={334} y1={624} x2={466} y2={624} stroke={FAINT} />
            <Shape as="circle" cx={400} cy={640} r={5} fill={grey[600]} />
          </motion.g>
        </InkContext.Provider>

        {/* Callouts, numbered as in TEARDOWN */}
        <Callout
          number={1}
          travel={cleaner.travel}
          visible={cleaner.callout}
          anchor={(t) => ({ x: 538, y: 179 - 230 * t })}
          offset={[56, -22]}
        />
        <Callout
          number={2}
          travel={carb.travel}
          visible={carb.callout}
          anchor={(t) => ({ x: 447, y: 222 - 150 * t })}
          offset={[86, -6]}
        />
        <Callout
          number={3}
          travel={intake.travel}
          visible={intake.callout}
          anchor={(t) => ({ x: 262, y: 262 - 95 * t })}
          offset={[-72, -18]}
        />
        <Callout
          number={4}
          travel={covers.travel}
          visible={covers.callout}
          anchor={(t) => at('L', 320 + 140 * t, 44)}
          offset={[42, -58]}
        />
        <Callout
          number={5}
          travel={heads.travel}
          visible={heads.callout}
          anchor={(t) => at('R', 285 + 105 * t, -54)}
          offset={[40, 56]}
        />
        <Callout
          number={6}
          travel={headers.travel}
          visible={headers.callout}
          anchor={(t) => at('L', 146 - 15 * t, -226 - 115 * t)}
          offset={[24, 58]}
        />
        <Callout
          number={7}
          travel={pistons.travel}
          visible={pistons.callout}
          anchor={(t) => at('R', 212 + 72 * t, -33)}
          offset={[48, 44]}
        />
        <Callout
          number={8}
          travel={crank.travel}
          visible={crank.callout}
          anchor={(t) => ({ x: 462, y: 500 + 115 * t })}
          offset={[72, 0]}
        />
        <Callout
          number={9}
          travel={pan.travel}
          visible={pan.callout}
          anchor={(t) => ({ x: 326, y: 640 + 200 * t })}
          offset={[-68, -6]}
        />
      </motion.g>
    </svg>
  );
};

export default V8Drawing;
